import React from "react";
import * as G from "../Grid";
import * as M from "../../Modal";
import * as H from "../../../hooks";
import * as S from "../../../services";
import * as HP from "../../../helpers";
import * as PM from "../../../PurposeModal";
import { CellsTypes as CT } from "../AgGridDefs";
import * as US from "../../../services/user.service";
import { TC, T, TABS, FP, RIGHTS, TB } from "../../../Constants";

type MaintenanceProps = {
    /** The context to load the gammes from */
    context?: T.ContextParams;
    /** An identifier for the table */
    origin?: string;
}

type Row = ReturnType<T.API.Utils.Actions.GetMaintenanceActionsRows>[number];

export const Maintenance: React.FC<MaintenanceProps> = props => {
    const lg = H.useLanguage();
    const [user] = H.useAuth();
    const rights = H.useRights();
    const [forms] = H.useFormIds();
    const [rows, set_rows, status] = H.useAsyncState<Row[]>([]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getMaintenanceActionsRows(props.context)
            .then(r => isSubscribed && set_rows(r.data, "done"))
            .catch(() => isSubscribed && set_rows([], "error"));
        return () => {
            isSubscribed = false;
            set_rows([], "load");
        }
    }, [props.context]);

    const header_id = React.useMemo(() => forms[FP.MAINTENANCE_ACTION], [forms]);

    const formatted_rows = React.useMemo(() => {
        const equipment_form = forms[FP.EQUIPEMENT_FORM];
        return rows.map(row => ({
            ...row,
            t_resources: (row.resources || []).map(r => lg.getStaticText(r)),
            t_unit: row.unit ? lg.getTextObj(equipment_form, row.unit, row.unit) : "",
            t_gammes: (row.gammes || []).map((g, i) => lg.getTextObj(g, "name", row.gammes_names?.[i] || "")),
        }));
    }, [rows, forms, lg]);

    const user_can = React.useMemo(() => ({
        edit: rights.isRightAllowed(RIGHTS.GGM_EDIT),
        create: rights.isRightAllowed(RIGHTS.GGM_CREATE),
        delete: rights.isRightAllowed(RIGHTS.GGM_DELETE),
    }), [rights]);

    const events = React.useMemo(() => ({
        edit: (row: Row, read = true) => {
            M.renderFormModal<T.MaintenanceAction>({ path: FP.MAINTENANCE_ACTION, submissionId: row._id, readOnly: read }).then(action => {
                if (action) S.getMaintenanceActionsRowsByIds(action._id)
                    .then(res => set_rows(p => p.map(r => res.data.find(a => a._id === r._id) || r)))
                    .catch(M.Alerts.loadError);
            });
        },
        remove: (rows: T.AllowArray<Row>) => {
            rows = TB.arrayWrapper(rows);
            const ids = rows.map(r => r._id);
            const title = rows.length > 1 ? TC.GLOBAL_DELET_N_ITEMS : TC.GLOBAL_DELETE;

            M.askConfirm({ title: lg.getStaticText(title, rows.length) }).then(confirmed => {
                if (confirmed) US.removeManySubmissionsFromId(ids)
                    .then(() => set_rows(p => p.filter(r => !ids.includes(r._id))))
                    .catch(M.Alerts.deleteError);
            });
        },
        create_global: (site_id?: string) => {
            const submission: Partial<T.MaintenanceAction> = {};
            if (site_id) submission.site = site_id;

            M.renderFormModal({ path: FP.MAINTENANCE_ACTION, forcedSubmission: TB.submissionToArrayUpdate(submission) }).then(action => {
                if (action) S.getMaintenanceActionsRowsByIds(action._id)
                    .then(res => set_rows(p => p.concat(res.data)))
                    .catch(M.Alerts.loadError);
            });
        },
        create_specific: () => {
            // Ask the user to pick a site
            PM.renderSiteSelect({ isRequired: true, context: props.context }).then(site_id => {
                if (site_id) events.create_global(site_id);
            });
        },
        import_standard_actions: () => {
            HP.standard_actions_import().then(actions => {
                if (actions) S.getMaintenanceActionsRowsByIds(actions.map(a => a._id))
                    .then(res => set_rows(p => p.concat(res.data)))
                    .catch(M.Alerts.loadError);
            });
        }
    }), [props.context, lg, set_rows]);

    const columns = React.useMemo<G.TableProps<Row>["columns"]>(() => [
        { field: "name", headerName: "name", params: { header_id } },
        { field: "site_name", headerName: "site", params: { header_id } },
        { field: "duration", headerName: "duration", params: { header_id } },
        { field: "t_resources", headerName: "resources", params: { header_id } },
        { field: "t_gammes", headerName: "gammes", params: { header_id } },
        { field: "description", headerName: "description", params: { header_id } },
        { field: "cost", headerName: "cost", params: { header_id }, type: CT.TYPE_NUMBER },
        { field: "t_unit", headerName: "unit", params: { header_id } },
        { field: "no_auto_create", headerName: "no_auto_create", params: { header_id, invert: true }, type: CT.TYPE_CHECKBOX },
        { field: "frequency", headerName: "frequency", params: { header_id }, type: CT.TYPE_FREQUENCY },
    ], [header_id]);

    const context_menu = React.useCallback<G.TableProps<Row>["getContextMenuItems"]>(event => {
        const row = event.node?.data;
        const selection = event.api.getSelectedRows();
        const context_items: ReturnType<G.TableProps<Row>["getContextMenuItems"]> = [];
        const is_editable_site_action = user_can.edit && TB.mongoIdValidator(row?.site);
        const is_editable_global_action = user.isAdmin && !TB.mongoIdValidator(row?.site);

        // Edit, Read and Delete
        if (row) {
            const is_global_action = !TB.mongoIdValidator(row.site);
            // Read option
            context_items.push({
                icon: "<i class='fa fa-search'></i>",
                action: () => events.edit(row),
                name: lg.getStaticText(TC.GLOBAL_SHOW),
            });
            // Edit option
            if (is_editable_site_action || is_editable_global_action) context_items.push({
                name: lg.getStaticText(TC.GLOBAL_EDIT),
                icon: "<i class='fa fa-pencil-alt'></i>",
                action: () => events.edit(row, false),
            });
            // Edition options (only for non global actions)
            if (!is_global_action) {
                // Delete options
                if (user_can.delete) {
                    // Delete One
                    context_items.push({
                        action: () => events.remove(row),
                        name: lg.getStaticText(TC.GLOBAL_DELETE),
                        icon: "<i class='fa fa-times text-danger'></i>",
                    })
                    // Delete many
                    if (selection.length > 1) context_items.push({
                        action: () => events.remove(selection),
                        icon: "<i class='fa fa-times text-danger'></i>",
                        name: lg.getStaticText(TC.GLOBAL_DELET_N_ITEMS, selection.length),
                    });
                }
            }
        }
        // Add a separator
        if (context_items.length > 0) context_items.push("separator");
        // Create site-specific action
        if (user_can.create) context_items.push({
            action: events.create_specific,
            icon: "<i class='fa fa-plus'></i>",
            name: lg.getStaticText(TC.MAINTENANCE_TABLE_NEW_STANDARD_ACTION_SITE),
        });
        // Create global action
        if (user.isAdmin) context_items.push({
            icon: "<i class='fa fa-plus'></i>",
            action: () => events.create_global(),
            name: lg.getStaticText(TC.NEW_GEN_ACTION_MAINTENANCE),
        });
        // Add a separator
        if (context_items.length > 0 && event.defaultItems?.length > 0) context_items.push("separator", ...event.defaultItems);
        return context_items;
    }, [events, user_can, user, lg, events]);

    const import_ref = React.useRef(events.import_standard_actions);
    React.useImperativeHandle(import_ref, () => events.import_standard_actions, [events]);

    const import_buttons = React.useMemo<G.TableProps<Row>["extra_buttons"]>(() => ({
        hidden: () => !user.isAdmin,
        onClick: import_ref.current,
        label: lg.getStaticText(TC.IMPORT_STANDARD_ACTIONS),
    }), [lg, user.isAdmin]);

    return <div className="w-100">
        <G.Table<Row>
            count
            sideBar
            status={status}
            columns={columns}
            rows={formatted_rows}
            columns_base="all_but_edit"
            extra_buttons={import_buttons}
            getRowId={row => row?.data?._id}
            getContextMenuItems={context_menu}
            adaptableId={props.origin || TABS.GESTION_MAINTENANCE}
        />
    </div>;
}

export const MaintenanceContext: React.FC = () => {
    H.useCrumbs(TC.GAMME);
    const [context] = H.useRoots();
    H.useAuth({ tabName: TABS.GESTION_MAINTENANCE })
    return <Maintenance context={context} origin={TABS.GESTION_MAINTENANCE} />;
}